<template>
  <rest-page ref="page" :fetch-endpoint="fetchEndpoint">
    <page-header-new
      :title="title"
      :tags="statuses"
      :breadcrumbs="breadcrumbs"
      :meta="meta"
    />
    <page-content v-if="device">
      <el-form>
        <el-row>
          <el-col :span="12">
            <device-information-card
              :title="$t('Profiles_Devices_Title_ApplicationInformation')"
              :items="applicationInformationItems"
            />
          </el-col>
          <el-col :span="12">
            <device-application-image-card
              :app-image-src="appImageSrc(device.application_icon)"
            />
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="12">
            <device-information-card
              :title="$t('Profiles_Devices_Title_DeviceInformation')"
              :items="
                isMobile
                  ? deviceInformationMobileItems
                  : deviceInformationWebsiteItems
              "
            />
          </el-col>
          <el-col :span="12">
            <device-image-card :image-src="deviceImageSrc" />
          </el-col>
        </el-row>

        <el-row>
          <el-col :span="12">
            <device-information-card
              :title="$t('Profiles_Devices_Title_PushNotifications')"
              :items="PushNotificationsItems"
            />
          </el-col>

          <el-col :span="12">
            <device-information-card
              :title="$t('Profiles_Devices_Title_GeoLocalization')"
              :items="GeoLocationItems"
            />
          </el-col>
        </el-row>
      </el-form>

      <device-attributes-list :readonly="readonly" :device-id="deviceId" />

      <device-authorised-profiles-list
        v-if="$hasFeature('allowAuthorisedDevices') && isAuthorised"
        :device-id="deviceId"
      />
    </page-content>
  </rest-page>
</template>

<script>
import RestPage from '@/common/components/rest/RestPage'
import PageHeaderNew from '@/common/components/page/PageHeaderNew'
import PageContent from '@/common/components/page/PageContent'
import DeviceInformationCard from '@/modules/devices/components/DeviceInformationCard'
import DeviceImageCard from '@/modules/devices/components/DeviceImageCard'
import DeviceAttributesList from '@/modules/devices/components/DeviceAttributesList'
import DeviceApplicationImageCard from '@/modules/devices/components/DeviceApplicationImageCard'
import DeviceAuthorisedProfilesList from '@/modules/devices/components/DeviceAuthorisedProfilesList'
import { projectEndpoint, projectRoute } from '@/core/helpers'
import { deviceDescription } from '@/modules/devices/helpers'
import moment from 'moment'
import { PushEnvironmentLabel } from '@/common/enum/PushEnvironmentEnum'
import { $hasFeature } from '@/core/utils/VueAcl'

export default {
  name: 'DevicePage',

  components: {
    RestPage,
    PageHeaderNew,
    PageContent,
    DeviceApplicationImageCard,
    DeviceInformationCard,
    DeviceImageCard,
    DeviceAttributesList,
    DeviceAuthorisedProfilesList
  },

  data() {
    return {
      PushEnvironmentLabel,
      profile: null,
      application: null,
      device: null,
      languages: [],
      countries: []
    }
  },

  computed: {
    fetchEndpoint() {
      return [
        this.fetchPageEndpoint,
        this.fetchLanguagesEndpoint,
        this.fetchCountriesEndpoint
      ]
    },

    fetchPageEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `devices/${this.deviceId}?expand=profile,application,authorised`,
        handler: data => {
          this.device = data
          this.profile = data.profile
          this.application = data.application
        }
      })
    },

    fetchLanguagesEndpoint() {
      return {
        method: 'get',
        url: `languages`,
        handler: data => {
          this.languages = data
        }
      }
    },

    fetchCountriesEndpoint() {
      return {
        method: 'get',
        url: `countries`,
        handler: data => {
          this.countries = data
        }
      }
    },

    readonly() {
      return !this.$can('Profile::Update')
    },

    username() {
      if (this.profile.name) {
        return this.profile.name
      } else if (this.profile.email) {
        return this.profile.email
      } else if (this.profile.user_id) {
        return `${this.$t('Profiles_Field_User')} ${this.profile.user_id}`
      } else {
        return this.$t('Profiles_Field_AnonymousUser')
      }
    },

    deviceId() {
      return this.$route.query.device_id || this.$route.query.id
    },

    isAuthorised() {
      return this.device.authorised === 1
    },

    breadcrumbs() {
      if (this.$route.query.profile_id) {
        return [
          {
            label: this.$t('Users_Breadcrumb_Data')
          },
          {
            label: this.$t('Users_Breadcrumb_Profiles'),
            route: projectRoute({
              name: 'Profiles'
            })
          },
          {
            label: this.$route.query.profile_id,
            route: projectRoute({
              name: 'ProfileOverview',
              query: {
                profile_id: this.$route.query.profile_id
              }
            })
          },
          {
            label: this.$t('Users_Breadcrumb_Devices'),
            route: projectRoute({
              name: 'ProfileDevices',
              query: {
                profile_id: this.$route.query.profile_id
              }
            })
          },
          {
            label: this.deviceId
          }
        ]
      } else {
        return [
          {
            label: this.$t('Users_Breadcrumb_Data')
          },
          {
            label: this.$t('Users_Breadcrumb_Devices'),
            route: projectRoute({
              name: 'Devices'
            })
          },
          {
            label: this.deviceId
          }
        ]
      }
    },

    title() {
      if (this.application && this.device) {
        return this.application.name + ' on ' + deviceDescription(this.device)
      } else {
        return null
      }
    },

    meta() {
      if (this.device && this.profile && this.application) {
        return [
          {
            label: this.username,
            icon: 'el-icon-user',
            route: projectRoute({
              name: 'ProfileOverview',
              query: {
                profile_id: this.profile.id
              }
            })
          },
          {
            label:
              this.installedDate !== null
                ? `${this.$t('Profiles_Devices_Text_InstalledAt')} ${
                    this.installedDate
                  }`
                : null,
            icon: 'el-icon-time'
          },
          {
            label:
              this.lastOpenedDate !== null
                ? `${this.$t('Profiles_Devices_Text_LastOpenedAt')} ${
                    this.lastOpenedDate
                  }`
                : null,
            icon: 'el-icon-time'
          }
        ]
      } else {
        return null
      }
    },

    statuses() {
      const result = []

      if (this.device) {
        if (this.device.active === 1) {
          result.push({
            type: 'success',
            label: this.$t('Profiles_Devices_Field_StatusActive')
          })
        } else {
          result.push({
            type: 'primary',
            label: this.$t('Profiles_Devices_Field_StatusInactive')
          })
        }
        if ($hasFeature('allowAuthorisedDevices') && this.isAuthorised) {
          result.push({
            class: 'device-authorised-tag',
            effect: 'plain',
            label: this.$t('Profiles_Devices_Field_Authorised')
          })
        }
      }

      return result
    },

    installedDate() {
      if (this.device.create_time) {
        return moment.unix(this.device.create_time).format('YYYY-MM-DD HH:mm')
      } else {
        return null
      }
    },

    lastOpenedDate() {
      if (this.device.open_time) {
        return moment.unix(this.device.open_time).format('YYYY-MM-DD HH:mm')
      } else {
        return null
      }
    },

    isMobile() {
      return this.device.type === 'ios' || this.device.type === 'android'
    },

    applicationInformationItems() {
      return [
        {
          label: this.$t('Profiles_Devices_Field_XtremepushId'),
          value: this.validateValueEmpty(this.device.id)
        },
        {
          label: this.$t('Profiles_Devices_Field_GoogleAnalyticsId'),
          value: this.validateValueEmpty(this.device.ga_id)
        },
        {
          label: this.$t('Profiles_Devices_Field_AppName'),
          value: this.validateValueEmpty(this.application.name)
        },
        {
          label: this.isMobile
            ? this.$t('Profiles_Devices_Field_BundleName')
            : this.$t('Profiles_Devices_Field_AppUrl'),
          value: !this.isMobile
            ? this.validateValueEmpty(this.application.web_url)
            : this.isMobile && this.device.type === 'ios'
            ? this.validateValueEmpty(this.application.ios_bundle_id)
            : this.validateValueEmpty(this.application.android_bundle_id)
        },
        {
          label: this.$t('Profiles_Devices_Field_AppVersion'),
          value: this.validateValueEmpty(this.device.app_version)
        },
        {
          label: this.$t('Profiles_Devices_Field_SdkVersion'),
          value: this.validateValueEmpty(this.device.lib_version)
        }
      ]
    },

    deviceInformationMobileItems() {
      return [
        {
          label: this.$t('Profiles_Devices_Field_Model'),
          value: this.validateValueEmpty(this.device.device_full_name)
        },
        {
          label: this.$t('Profiles_Devices_Field_Os'),
          value: this.validateValueEmpty(this.device.os)
        },
        {
          label: this.$t('Profiles_Devices_Field_Carrier'),
          value: this.validateValueEmpty(this.device.carrier_name)
        },
        {
          label: this.$t('Profiles_Devices_Field_UserAgent'),
          value: this.validateValueEmpty(this.device.user_agent)
        },
        ...this.deviceInformationMobileIds
      ]
    },

    deviceInformationMobileIds() {
      if (this.device.type === 'ios') {
        let ids = [
          {
            label: this.$t('Profiles_Devices_Field_IDFV'),
            value: this.validateValueEmpty(this.device.device_idfv)
          },
          {
            label: this.$t('Profiles_Devices_Field_IDFA'),
            value: this.validateValueEmpty(this.device.device_idfa)
          }
        ]

        if (this.device.device_id !== this.device.device_idfv) {
          ids.push({
            label: this.$t('Profiles_Devices_Field_Original_IDFV'),
            value: this.validateValueEmpty(this.device.device_id)
          })
        }

        return ids
      } else {
        if (this.device.type === 'android') {
          return [
            {
              label: this.$t('Profiles_Devices_Field_ANID'),
              value: this.validateValueEmpty(this.device.device_anid)
            },
            {
              label: this.$t('Profiles_Devices_Field_ADID'),
              value: this.validateValueEmpty(this.device.device_adid)
            }
          ]
        }
      }
    },

    deviceInformationWebsiteItems() {
      return [
        {
          label: this.$t('Profiles_Devices_Field_Model'),
          value: this.validateValueEmpty(this.device.device_full_name)
        },
        {
          label: this.$t('Profiles_Devices_Field_Os'),
          value: this.validateValueEmpty(this.device.os)
        },
        {
          label: this.$t('Profiles_Devices_Field_Browser'),
          value: this.validateValueEmpty(this.device.browser_name)
        },
        {
          label: this.$t('Profiles_Devices_Field_BrowserVersion'),
          value: this.validateValueEmpty(this.device.browser_version)
        },
        {
          label: this.$t('Profiles_Devices_Field_DeviceType'),
          value: this.validateValueEmpty(this.device.device_type)
        },
        {
          label: this.$t('Profiles_Devices_Field_UserAgent'),
          value: this.validateValueEmpty(this.device.user_agent)
        }
      ]
    },

    PushNotificationsItems() {
      let items = [
        {
          label: this.$t('Profiles_Devices_Field_PushEnvironment'),
          value: this.$t(
            this.validateValueEmpty(
              this.PushEnvironmentLabel[this.device.environment]
            )
          )
        },
        {
          label: this.$t('Profiles_Devices_Field_Token'),
          value: this.validateValueEmpty(this.device.token)
        },
        {
          label: this.$t('Profiles_Devices_Field_PushAddressable'),
          value: this.validatePushAddressableStatus(this.device.addressable),
          icon: this.validatePushAddressableStatusIcon(this.device.addressable)
        },
        {
          label: this.$t('Profiles_Devices_Field_PushSubscription'),
          value: this.validatePushNotificationStatus(this.device.subscription),
          icon: this.validatePushNotificationStatusIcon(
            this.device.subscription
          )
        }
      ]

      if (this.device.type === 'web') {
        items.push({
          label: this.$t('Profiles_Devices_Field_PushKeys'),
          value: this.validateValueEmpty(this.device.push_keys)
        })
      }

      return items
    },

    GeoLocationItems() {
      return [
        {
          label: this.$t('Profiles_Devices_Field_Country'),
          value: this.validateValueEmpty(this.countries[this.device.country]),
          flag:
            this.validateValueEmpty(this.device.country) !== '-'
              ? this.device.country
              : null
        },
        {
          label: this.$t('Profiles_Devices_Field_DeviceLanguage'),
          value: this.validateValueEmpty(this.languages[this.device.language])
        },
        {
          label: this.$t('Profiles_Devices_Field_Timezone'),
          value: this.validateValueEmpty(this.device.timezone)
        },
        {
          label: this.$t('Profiles_Devices_Field_AppLanguage'),
          value: this.validateValueEmpty(
            this.languages[this.device.language_app]
          )
        }
      ]
    },

    deviceImageSrc() {
      if (this.device.device_image) {
        return this.device.device_image
      } else if (this.device.os_logo) {
        return this.device.os_logo
      } else {
        return require('@/img/devices/image-not-available.png')
      }
    }
  },

  methods: {
    validateValueEmpty(propValue) {
      if (propValue) {
        return propValue
      } else {
        return '-'
      }
    },

    validatePushNotificationStatus(propValue) {
      if (propValue === 0 || propValue === false) {
        return this.$t('Profiles_Devices_Text_No')
      } else if (propValue === 1 || propValue === true) {
        return this.$t('Profiles_Devices_Text_Yes')
      } else {
        return '-'
      }
    },

    validatePushNotificationStatusIcon(propValue) {
      if (propValue === 0 || propValue === false) {
        return 'el-icon-empty-icon'
      } else {
        return 'el-icon-success'
      }
    },

    validatePushAddressableStatus(propValue) {
      if (
        (propValue === 1 || propValue === true) &&
        (this.device.active === 1 || this.device.active === true)
      ) {
        return this.$t('Profiles_Devices_Text_Yes')
      } else {
        return this.$t('Profiles_Devices_Text_No')
      }
    },

    validatePushAddressableStatusIcon(propValue) {
      if (
        (propValue === 1 || propValue === true) &&
        (this.device.active === 1 || this.device.active === true)
      ) {
        return 'el-icon-success'
      } else {
        return 'el-icon-empty-icon'
      }
    },

    appImageSrc(image) {
      if (image) {
        return image
      } else {
        return require('@/img/devices/image-not-available.png')
      }
    }
  }
}
</script>

<style scoped>
>>> .el-row {
  display: flex;
  margin-bottom: 20px;
}

>>> .el-col > * {
  width: 100%;
  height: 100%;
}

>>> .device-authorised-tag {
  border-color: #b3d8ff;
  color: #409eff;
}
</style>



// WEBPACK FOOTER //
// src/modules/devices/pages/DevicePage.vue